import { TabulatorFull } from "tabulator-tables";



import type { IPaymentMethodReportRow, IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { replaceString } from "@/data/util/String";



import { toMoney } from "@/shared/utils/format";



import { OperatorClient } from "@/operator/OperatorClient";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { BaseController, ViewTransition } from "@/operator/controller/BaseController";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { UserMenu } from "@/operator/ui/UserMenu";
import { FulfillmentSoundNotifications, SoundNotifications } from "@/operator/ux/SoundNotifications";


class TableRow {
    paymentMethod: string;
    quantityNet: string;
    quantity: string;
    cancellationQuantity: string;
    grossNetSales: string;
    grossNetSalesPercent: string;
    grossNetSalesAvg: string;
    tipNet: string;
    toRemit: string;

    constructor(
        paymentMethod: string,
        netQuantity: string,
        quantity: string,
        cancellationQuantity: string,
        grossNetSales: string,
        grossNetSalesPercent: string,
        grossNetSalesAvg: string,
        tipNet: string,
        toRemit: string
    ) {
        this.paymentMethod = paymentMethod;
        this.quantityNet = netQuantity;
        this.quantity = quantity;
        this.cancellationQuantity = cancellationQuantity;
        this.grossNetSales = grossNetSales;
        this.grossNetSalesPercent = grossNetSalesPercent;
        this.grossNetSalesAvg = grossNetSalesAvg;
        this.tipNet = tipNet;
        this.toRemit = toRemit;
    }
}

export class PaymentMethodsReportController extends BaseController {
    private productionContext: ProductionContext;
    private readonly $head: JQuery;
    private readonly $scrollable: JQuery;
    private readonly $dateFilter: JQuery;
    private readonly $headerFilter: JQuery;
    private table?: any;
    private data: TableRow[];

    constructor() {
        super("payment-methods-report");
        this.productionContext = ProductionContext.getInstance();

        this.data = [];

        this.$head = $('<div class="head"><div class="logo">tastier</div></div>').appendTo(this.$view);
        new UserMenu().appendTo(this.$head);

        this.$scrollable = $('<div class="scrollable"></div>').appendTo(this.$view);
        this.$dateFilter = $('<div style="padding: 12px;"></div>').appendTo(this.$scrollable);

        this.$headerFilter = $(
            '<div class="font-weight-normal" style="padding-left: 12px; padding-bottom: 12px;"></div>'
        ).appendTo(this.$scrollable);
        $('<div id="payment-methods-report-table"></div>').appendTo(this.$scrollable);
    }

    public showView(data: IPaymentMethodsReportData, transition?: ViewTransition) {
        this.data = this.ConvertPaymentMethodsToRows(data.data);
        this.data.push(this.ConvertTotalToRow(data.totals));

        if (!this.isVisible()) {
            this.show(transition);
        }

        const now = new Date();
        this.$dateFilter.empty();
        this.$headerFilter.empty();
        $(
            '<div class="font-weight-normal">' +
                replaceString(
                    trans.salesReports.dateRangeGenerated,
                    DateTime.toPreciseDateTimeString(now),
                    DateTime.toPreciseDateTimeString(data.filters.startDate),
                    DateTime.toPreciseDateTimeString(data.filters.endDate)
                ) +
                "</div>"
        ).appendTo(this.$dateFilter);

        let filterText = "";
        if (data.filters.userName != null && data.filters.userName != "") {
            filterText += trans.general.entities.users + " <b>" + data.filters.userName + "</b>&ensp;&ensp;";
        }

        if (data.filters.filterByEndUsers) {
            filterText += "<b>" + trans.salesReports.endUsers + "</b>&ensp;&ensp;";
        }

        if (data.filters.rooms != null && data.filters.rooms.length > 0) {
            filterText +=
                trans.general.entities.rooms +
                " <b>" +
                data.filters.rooms.map((r) => r.name).join(", ") +
                "</b>&ensp;&ensp;";
        }

        if (data.filters.offers != null && data.filters.offers.length > 0) {
            filterText +=
                trans.general.entities.offers +
                " <b>" +
                data.filters.offers.map((r) => r.name).join(", ") +
                "</b>&ensp;&ensp;";
        }

        if (data.filters.fulfillmentGroups != null && data.filters.fulfillmentGroups.length > 0) {
            filterText +=
                trans.general.entities.fulfillmentGroups +
                " <b>" +
                data.filters.fulfillmentGroups
                    .map((r) => Format.translate(r.name, OperatorClient.context.settings?.location?.language!))
                    .join(", ") +
                "</b>&ensp;&ensp;";
        }

        this.$headerFilter.html(filterText);

        this.initializeTable();
    }

    protected onBecomeVisible() {
        operatorEvents.productionContext.FulfillmentAdded.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);

    }

    protected onBecomeInvisible() {
        operatorEvents.productionContext.FulfillmentAdded.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);

    }

    private ConvertPaymentMethodsToRows(paymentMethod: IPaymentMethodReportRow[]): TableRow[] {
        return paymentMethod.map((c) => {
            const grossNetSalesPercent = (c.grossNetSalesPercent * 100).toFixed(2) + "%";
            return new TableRow(
                c.paymentMethodName,
                c.quantityNet.toString(),
                c.quantity.toString(),
                (c.cancelledQuantity * -1).toString(),
                toMoney(c.grossSalesInclTips, true),
                grossNetSalesPercent,
                toMoney(c.grossNetSalesAvg, true),
                toMoney(c.tipNet, true),
                c.toRemit == null ? "" : toMoney(c.toRemit, true),
            );
        });
    }

    private ConvertTotalToRow(paymentMethod: IPaymentMethodReportRow): TableRow {
        const grossNetSalesPercent = (paymentMethod.grossNetSalesPercent * 100).toFixed(2) + "%";
        return new TableRow(
            trans.orderDetails.total,
            paymentMethod.quantityNet.toString(),
            paymentMethod.quantity.toString(),
            (paymentMethod.cancelledQuantity * -1).toString(),
            toMoney(paymentMethod.grossSalesInclTips, true),
            grossNetSalesPercent,
            toMoney(paymentMethod.grossNetSalesAvg, true),
            toMoney(paymentMethod.tipNet, true),
            ""
        );
    }

    private initializeTable() {
        if (this.table === undefined) {
            this.table = new TabulatorFull("#payment-methods-report-table", {
                maxHeight: "100%",
                height: "100%",
                data: this.data,
                selectable: true,
                dataTree: true,
                layout: "fitData",
                dataTreeCollapseElement: "<i style='width: 24px;' class='fas fa-minus-square'></i>",
                dataTreeExpandElement: "<i style='width: 24px;' class='fas fa-plus-square'></i>",
                renderHorizontal: "virtual",
                dataTreeChildIndent: 20,
                dataTreeStartExpanded: false,
                columns: [
                    {
                        title: trans.paymentMethodsReport.paymentMethod,
                        field: "paymentMethod",
                        resizable: "header",
                        width: 240,
                        sorter: "string",
                        headerSort: false,
                        frozen: true,
                    },
                    {
                        title: trans.paymentMethodsReport.netQuantity,
                        field: "quantityNet",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.quantity,
                        field: "quantity",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.cancellationQuantity,
                        field: "cancellationQuantity",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.grossSalesNetChfInclTip,
                        field: "grossNetSales",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.grossSalesPercent,
                        field: "grossNetSalesPercent",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.grossSalesAvg,
                        field: "grossNetSalesAvg",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.tipNet,
                        field: "tipNet",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.paymentMethodsReport.toRemit,
                        field: "toRemit",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                ],
            });
        } else {
            this.table.setData(this.data);
        }
    }
}
